import { Alert, App as AntApp, Button, Modal, Popconfirm, Segmented, Space, Table, Tag, Tooltip } from "antd";
import type { TableColumnsType } from "antd";
import { DeleteOutlined, EditOutlined, HistoryOutlined, StopOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ApiError } from "../app/apiError.ts";
import { useAuth } from "../app/auth.tsx";
import { billingKeys, formatDate, todayIso, type RuleVersion } from "../app/billing.ts";
import { formatSum, toAmount } from "../app/balance.ts";
import { AuditHistory } from "./AuditHistory.tsx";

/** "1 250 000 so‘m". */
export function SumText({ value }: { value: number | string }) {
  const { t } = useTranslation();
  return <>{t("billing.sum", { amount: formatSum(toAmount(value)) })}</>;
}

/** "01.09.2026 – 31.12.2026" or "01.09.2026 dan" for an open-ended version. */
export function ValidityText({ version }: { version: RuleVersion }) {
  const { t } = useTranslation();
  return version.validTo
    ? t("billing.validity.range", { from: formatDate(version.validFrom), to: formatDate(version.validTo) })
    : t("billing.validity.openEnded", { from: formatDate(version.validFrom) });
}

/** In force / upcoming / ended, plus whether an invoice already used the version (then it is frozen). */
export function VersionStatus({ version }: { version: RuleVersion }) {
  const { t } = useTranslation();
  const today = todayIso();
  const state = version.validFrom > today ? "upcoming" : version.validTo && version.validTo < today ? "ended" : "current";
  const color = { upcoming: "blue", ended: "default", current: "green" }[state];
  return (
    <Space size={4} wrap>
      <Tag color={color}>{t(`billing.status.${state}`)}</Tag>
      {version.usedThrough && (
        <Tooltip title={t("billing.status.usedHint")}>
          <Tag color="orange">{t("billing.status.used", { date: formatDate(version.usedThrough) })}</Tag>
        </Tooltip>
      )}
    </Space>
  );
}

/** Switch between the versions in force today and every version. */
export function ScopeSwitch({ all, onChange }: { all: boolean; onChange(all: boolean): void }) {
  const { t } = useTranslation();
  return (
    <Segmented<"current" | "all">
      value={all ? "all" : "current"}
      onChange={(value) => onChange(value === "all")}
      options={[
        { value: "current", label: t("billing.scope.current") },
        { value: "all", label: t("billing.scope.all") },
      ]}
    />
  );
}

/** Deletes a version through the given call, refreshing the lists and the audit trail; errors are shown as a message. */
export function useDeleteVersion(remove: (id: string) => Promise<{ error?: unknown; response: Response }>) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error, response } = await remove(id);
      if (!response.ok) throw new ApiError(response.status, error);
    },
    onSuccess: async () => {
      await Promise.all([queryClient.invalidateQueries({ queryKey: billingKeys.all }), queryClient.invalidateQueries({ queryKey: ["audit"] })]);
      void message.success(t("billing.deleted"));
    },
    onError: (err) => {
      const lines = err instanceof ApiError ? err.messages(t("app.error")) : [t("app.error")];
      void message.error(lines.join(" "));
    },
  });
}

interface ActionsProps {
  version: RuleVersion;
  label: string;
  canManage: boolean;
  deleting: boolean;
  onHistory(): void;
  onEdit(): void;
  onDelete(): void;
  onEnd(): void;
}

/**
 * History for everyone; correcting and deleting only for an unused version, and deleting only the open (latest) one.
 * Ending applies to the open version, used or not (bd-2sz.14).
 */
export function VersionActions({ version, label, canManage, deleting, onHistory, onEdit, onDelete, onEnd }: ActionsProps) {
  const { t } = useTranslation();
  const editable = canManage && !version.usedThrough;
  return (
    <Space size={0}>
      <Tooltip title={t("billing.actions.history")}>
        <Button type="text" icon={<HistoryOutlined />} aria-label={t("billing.actions.historyNamed", { name: label })} onClick={onHistory} />
      </Tooltip>
      {editable && (
        <Tooltip title={t("billing.actions.edit")}>
          <Button type="text" icon={<EditOutlined />} aria-label={t("billing.actions.editNamed", { name: label })} onClick={onEdit} />
        </Tooltip>
      )}
      {canManage && !version.validTo && (
        <Tooltip title={t("billing.end.action")}>
          <Button type="text" icon={<StopOutlined />} aria-label={t("billing.end.actionNamed", { name: label })} onClick={onEnd} />
        </Tooltip>
      )}
      {editable && !version.validTo && (
        <Popconfirm
          title={t("billing.actions.deleteConfirm")}
          description={t("billing.actions.deleteHint")}
          okText={t("common.delete")}
          cancelText={t("common.cancel")}
          onConfirm={onDelete}
        >
          <Button type="text" danger loading={deleting} icon={<DeleteOutlined />} aria-label={t("billing.actions.deleteNamed", { name: label })} />
        </Popconfirm>
      )}
    </Space>
  );
}

interface HistoryProps<T extends RuleVersion> {
  kind: string;
  entityType: string;
  version: T | undefined;
  title: string;
  columns: TableColumnsType<T>;
  load(id: string): Promise<T[]>;
  onClose(): void;
}

/** Every version of one key, newest first; with audit.read each row expands to its change history. */
export function RuleHistoryModal<T extends RuleVersion>({ kind, entityType, version, title, columns, load, onClose }: HistoryProps<T>) {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const id = version?.id ?? "";
  const history = useQuery({ queryKey: billingKeys.history(kind, id), queryFn: () => load(id), enabled: version !== undefined });

  return (
    <Modal open={version !== undefined} title={title} footer={null} width={900} onCancel={onClose} destroyOnHidden>
      {history.isError && <Alert type="error" showIcon title={t("app.error")} action={<Button onClick={() => void history.refetch()}>{t("app.retry")}</Button>} />}
      <Table<T>
        rowKey="id"
        size="small"
        loading={history.isPending}
        columns={columns}
        dataSource={history.data ?? []}
        pagination={false}
        scroll={{ x: true }}
        locale={{ emptyText: t("billing.empty") }}
        {...(hasPermission("audit.read") ? { expandable: { expandedRowRender: (row: T) => <AuditHistory entityType={entityType} entityId={row.id} /> } } : {})}
      />
    </Modal>
  );
}
